import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

/**
 * An event created when a Medic prescibes medical imaging to a Patient.
 * It contains fields letting the Medic enter medical imaging or exam manually.
 */
export default class MedicalImagingPrescribedJournalEvent
  extends JournalEvent
  implements PrescriptionJournalEvent
{
  static override type = "medical-imaging-prescribed-journal-event";

  /**
   * provides the right info about the patient and its health center
   */
  @attr() declare snapshot: any;

  /**
   * The nature of the prescribed imaging or exam linked to the ALD
   */
  @attr("string") declare typeOfExamAld: string;

  /**
   * The clinical state of the patient
   */
  @attr("string") declare patientClinicalStateAld: string;

  /**
   * Some indications related to the imaging or exam
   */
  @attr("string") declare indicationsAld: string;

  /**
   * The nature of the prescribed imaging or exam not linked to the ALD
   */
  @attr("string") declare typeOfExamNonAld: string;

  /**
   * The clinical state of the patient
   */
  @attr("string") declare patientClinicalStateNonAld: string;

  /**
   * Some indications related to the imaging or exam
   */
  @attr("string") declare indicationsNonAld: string;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.medical-imaging-prescribed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "medicalImagingPrescribedJournalEvents"
  })
  declare patient: Patient;

  @attr("string")
  declare uuid: string;

  get startDate(): Date {
    return this.occurredAt;
  }

  get endDate(): Date {
    const endDate = new Date(this.startDate);
    endDate.setMonth(endDate.getMonth() + 12);

    return endDate;
  }

  async renew(): Promise<MedicalImagingPrescribedJournalEvent> {
    const prescriptionRenewed = await this.store
      .createRecord("medical-imaging-prescribed-journal-event", {
        occurredAt: new Date(),
        patient: this.patient,
        typeOfExamAld: this.typeOfExamAld,
        patientClinicalStateAld: this.patientClinicalStateAld,
        indicationsAld: this.indicationsAld,
        typeOfExamNonAld: this.typeOfExamNonAld,
        patientClinicalStateNonAld: this.patientClinicalStateNonAld,
        indicationsNonAld: this.indicationsNonAld
      })
      .save();

    return prescriptionRenewed;
  }

  get pdfUrl() {
    return `/api/v1/medical_imaging_prescriptions/${this.id}`;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "medical-imaging-prescribed-journal-event": MedicalImagingPrescribedJournalEvent;
  }
}
